import * as React from 'react'
import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'

import { useSplit, useSplitEnabled } from '@buffer-mono/features'
import { useIsMobileScreenSize } from '@buffer-mono/popcorn'

import BillingBanner from '../BillingBanner/BillingBanner'
import ChannelRefreshBanner from '../ChannelRefreshBanner/ChannelRefreshBanner'
import { shouldShowChannelRefreshBanner } from '../../utils/shouldShowChannelRefreshBanner'
import { useUser } from '../../../../common/context/User'
import { trackBannerViewed } from '../../../../common/hooks/useSegmentTracking'
import {
  getBillingBannerSlug,
  getVisibleBillingConditions,
} from '../../utils/billingBannerConditions'
import TrialBanner from '../TrialBanner'
import {
  hasPermissionToManageBilling,
  hasPaymentDetails,
  isObjectDismissed,
  isOnActiveTrial,
  isOneBufferOrganization,
} from '../../../../common/utils/user'
import TeamMemberOrgDowngradedBanner from '../TeamMemberOrgDowngradedBanner/TeamMemberOrgDowngradedBanner'
import AnnualUpgradeBanner from '../AnnualUpgradeBanner/AnnualUpgradeBanner'
import {
  daysBeforeShowingForTreatment,
  isEligibleForAnnualUpgrade,
} from '../../utils/shouldShowAnnualUpgradeBanner'

import styles from './BannerContainer.module.css'

const BannerContainer = (): JSX.Element => {
  const user = useUser()

  const billingBannerSlug = getBillingBannerSlug(
    getVisibleBillingConditions(user),
  )

  const { isEnabled: isIgLoginRefreshBannerEnabled } = useSplitEnabled(
    'ig-login-refresh-banner',
  )

  const showChannelRefreshBanner = shouldShowChannelRefreshBanner(user, {
    isIgLoginRefreshEnabled: isIgLoginRefreshBannerEnabled,
  })

  const { split: geidMonthlyToAnnualSplit, isReady: isGeidSplitReady } =
    useSplit('geid-monthly-to-annual')

  const daysBeforeShowing = daysBeforeShowingForTreatment(
    geidMonthlyToAnnualSplit.treatment,
  )
  const isEligibleForAnnualUpgradeForArm =
    daysBeforeShowing !== null &&
    isEligibleForAnnualUpgrade(user, daysBeforeShowing)

  // Hide the annual upgrade banner on mobile web, where it pushes content far
  // down the screen.
  const isMobile = useIsMobileScreenSize()

  const showAnnualUpgradeBanner =
    !isMobile &&
    isGeidSplitReady &&
    isEligibleForAnnualUpgradeForArm &&
    !isObjectDismissed(user, 'annual-upgrade-banner')

  let showTrialBanner = false
  if (
    isOneBufferOrganization(user) &&
    hasPermissionToManageBilling(user) &&
    !hasPaymentDetails(user)
  ) {
    showTrialBanner = isOnActiveTrial(user)
  }

  const shouldShowTeamMemberOrganizationDowngradedBanner =
    user.currentOrganization
      ?.shouldShowTeamMemberOrganizationDowngradedBanner || false

  /* we need to memoize this value as it is an array and it will be regenerated on every render in other case */
  const bannerConfig = useMemo(() => {
    return [
      {
        condition: billingBannerSlug !== null,
        component: BillingBanner,
        slug: billingBannerSlug ?? 'billing',
      },
      {
        condition: showChannelRefreshBanner,
        component: ChannelRefreshBanner,
        slug: 'channel-refresh',
      },
      {
        condition: showAnnualUpgradeBanner,
        component: AnnualUpgradeBanner,
        slug: 'annual-upgrade-banner',
        extraProps: { treatment: geidMonthlyToAnnualSplit.treatment },
      },
      {
        condition: showTrialBanner,
        component: TrialBanner,
        slug: 'trial-upgrade',
      },
      {
        condition: shouldShowTeamMemberOrganizationDowngradedBanner,
        component: TeamMemberOrgDowngradedBanner,
        slug: 'team-member-organization-downgraded',
      },
    ]
  }, [
    billingBannerSlug,
    showChannelRefreshBanner,
    showAnnualUpgradeBanner,
    geidMonthlyToAnnualSplit.treatment,
    showTrialBanner,
    shouldShowTeamMemberOrganizationDowngradedBanner,
  ])

  /* we need to memoize this value as it is an array and it will be regenerated on every render */
  const bannerViewed = useMemo(() => {
    return bannerConfig
      .filter((banner) => banner.condition)
      .map((banner) => banner.slug)
  }, [bannerConfig])

  useEffect(() => {
    trackBannerViewed({
      payload: {
        bannerViewed,
      },
      user,
    })
  }, [bannerViewed, user])

  return (
    <div className={styles.container}>
      {bannerConfig.map((banner) =>
        banner.condition ? (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <banner.component key={banner.slug} {...(banner.extraProps || {})} />
        ) : null,
      )}
    </div>
  )
}

/* Thi savoids this component to re-render on every parent re-render */
export default React.memo(BannerContainer)
